import { isReduceMotionEnabled } from './reduce-motion';

/**
 * One-shot hologram glitch for any element: adds a class whose keyframes (app.css,
 * `holo-glitch` / `holo-glitch-hard`) tear, offset and chromatically split it for a
 * fraction of a second, then removes the class again.
 *
 * The keyframes only touch properties nothing else here animates or sets — the
 * individual `translate`/`scale`/`rotate` properties, `filter`, `clip-path`,
 * `opacity` — never `transform`, so a panel's resting perspective pose is intact
 * before, during and after a glitch.
 */

export type GlitchStrength = 'soft' | 'hard';

const CLASS: Record<GlitchStrength, string> = { soft: 'holo-glitch', hard: 'holo-glitch--hard' };
/** Matches the keyframe lengths in app.css, plus a little slack; also the safety net if `animationend` never fires. */
const DURATION_MS: Record<GlitchStrength, number> = { soft: 520, hard: 950 };

const timers = new WeakMap<Element, number>();

export function motionAllowedForGlitch(): boolean {
  if (isReduceMotionEnabled()) return false;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return true;
}

/** Plays one glitch on `el`. Restarts cleanly if it is already glitching. A no-op when motion is reduced. */
export function glitchOnce(el: Element, strength: GlitchStrength = 'soft'): void {
  if (!motionAllowedForGlitch()) return;
  clearGlitch(el);
  // Forces the removed class to take effect so re-adding it restarts the animation.
  void (el as HTMLElement).offsetWidth;
  el.classList.add(CLASS[strength]);
  timers.set(
    el,
    window.setTimeout(() => clearGlitch(el), DURATION_MS[strength]),
  );
}

function clearGlitch(el: Element): void {
  const t = timers.get(el);
  if (t !== undefined) window.clearTimeout(t);
  timers.delete(el);
  el.classList.remove(CLASS.soft, CLASS.hard);
}

const CLAIM_MS = 1100;

/**
 * The celebratory "claim" glitch (app.css `holo-claim`). Resolves once the effect has played, so
 * the caller can keep the element on screen until then. Resolves immediately — with no effect — when
 * motion is reduced, or in an environment that cannot animate at all (jsdom).
 */
export function claimBurst(el: HTMLElement): Promise<void> {
  if (!motionAllowedForGlitch() || typeof el.getAnimations !== 'function') return Promise.resolve();
  clearGlitch(el);
  void el.offsetWidth;
  el.classList.add('holo-claim');
  return new Promise((resolve) => window.setTimeout(resolve, CLAIM_MS));
}
